import { useState, useEffect } from 'react';
import { Button, Card, Col, Container, Row, Alert, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Logout } from '../../../../store/actions/AuthActions';
import ManagerSubscriptionsApiClient from '../../../../api-client/ManagerSubscriptionsApiClient';
import { useProfile } from '../../../../hooks/useProfile';
import { store } from '../../../../store/store';
import { conMotivo } from "../../../components/Util/erroreServer";
import { useManagerSubscription } from '../../../../context/ManagerSubscriptionContext';

const SuccessPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get('session_id');
    const { fetchStatus, subscription } = useManagerSubscription();
    const dispatch = useDispatch();
    const { profile } = useProfile();

    const handleLogout = () => {
        dispatch(Logout(navigate));
    };

    const [type] = useState('business');
    const [formData, setFormData] = useState({
        vatNumber: '',
        taxCode: '',
        denomination: '',
        firstName: '',
        lastName: '',
        address: '',
        zipCode: '',
        city: '',
        province: '',
        nation: 'IT',
        pec: '',
        sdiCode: ''
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (subscription?.billingData) {
            const b = subscription.billingData;
            setFormData({
                vatNumber: b.vatNumber || '',
                taxCode: b.taxCode || '',
                denomination: b.denomination || '',
                firstName: b.firstName || '',
                lastName: b.lastName || '',
                address: b.address || '',
                zipCode: b.zipCode || '',
                city: b.city || '',
                province: b.province || '',
                nation: b.nation || 'IT',
                pec: b.pec || '',
                sdiCode: b.sdiCode || ''
            });
        } else if (profile?.club?.name && !formData.denomination) {
            setFormData(prev => ({ ...prev, denomination: profile.club.name }));
        }
    }, [subscription?.billingData, profile?.club?.name]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const isMissingBilling = subscription && subscription.active && subscription.status !== 'free' && subscription.billingDataInserted === false;
        const isPending = subscription?.paymentPending;
        if (!sessionId && !isMissingBilling && !isPending) {
            navigate('/abbonamento', { replace: true });
        }
    }, [sessionId, navigate, subscription]);

    useEffect(() => {
        if (!sessionId) return;
        const isProd = typeof window !== 'undefined' && window.location.hostname === 'manager.sportid.it';
        if (!isProd) return;
        if (typeof window.gtag === 'function') {
            window.gtag('event', 'conversion', {
                send_to: 'AW-17225121954/x656CMurhp0cEKKBypVA',
                transaction_id: sessionId,
            });
        }
        if (typeof window.fbq === 'function') {
            window.fbq('track', 'Purchase', {
                value: 149.00,
                currency: 'EUR',
                content_name: 'SportId Manager - Startup All Inclusive',
                content_type: 'product',
            }, { eventID: sessionId });
        }
    }, [sessionId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        if (type === 'business' && !formData.pec.trim()) {
            setError('Per le aziende è obbligatorio inserire la PEC.');
            setLoading(false);
            return;
        }

        if ((formData.vatNumber || '').trim().toUpperCase().startsWith('IT')) {
            setError('La Partita IVA non deve iniziare con "IT". Inserisci solo le 11 cifre numeriche.');
            setLoading(false);
            return;
        }

        try {
            const token = store.getState().auth.auth.idToken;
            const apiClient = new ManagerSubscriptionsApiClient();
            
            const payload = {
                sessionId,
                type,
                address: formData.address,
                zipCode: formData.zipCode,
                city: formData.city,
                province: formData.province,
                nation: 'IT',
                pec: formData.pec,
                sdiCode: formData.sdiCode
            };

            payload.vatNumber = formData.vatNumber;
            payload.denomination = formData.denomination;
            payload.taxCode = formData.taxCode;

            const res = await apiClient.saveBillingData(token, payload);
            
            if (res === true) {
                setSuccess(true);
                // Refresh subscription status to unlock access
                await fetchStatus();
                setTimeout(() => {
                    navigate('/struttura/dashboard', { replace: true });
                }, 3000);
            } else {
                setError(res.error || 'Errore durante il salvataggio dei dati di fatturazione.');
            }
        } catch (err) {
            setError(conMotivo('Si è verificato un errore.'));
        } finally {
            setLoading(false);
        }
    };

    const isMissingBilling = subscription && subscription.active && subscription.status !== 'free' && subscription.billingDataInserted === false;
    const isPending = subscription?.paymentPending;
    if (!sessionId && !isMissingBilling && !isPending) return null;

    return (
        <Container fluid className="h-100 pb-5">
            <Row className="justify-content-center align-items-center h-100">
                <Col md={8}>
                    <Card className="shadow-sm">
                        <Card.Body className="p-5">
                            {success ? (
                                <div className="text-center">
                                    <h2 className="text-success mb-4">Pagamento Riuscito!</h2>
                                    <p>I tuoi dati sono stati salvati correttamente.</p>
                                    <p>Ti stiamo reindirizzando alla dashboard...</p>
                                </div>
                            ) : isPending ? (
                                <div className="text-center">
                                    <h4 className="mb-3">Bonifico in attesa di accredito</h4>
                                    <Alert variant="info" className="text-start">
                                        <p className="mb-2">
                                            Abbiamo registrato la tua richiesta. Per completare l'attivazione effettua
                                            il bonifico usando le <b>coordinate (IBAN, importo e causale)</b> che ti ha
                                            mostrato Stripe al termine del pagamento.
                                        </p>
                                        <p className="mb-2"><b>Cosa succede ora, in ordine:</b></p>
                                        <ol className="mb-2 ps-3">
                                            <li>Esegui il bonifico dalla tua banca con le coordinate ricevute.</li>
                                            <li>
                                                Appena riceviamo i fondi (di norma <b>1–3 giorni lavorativi</b>)
                                                l'abbonamento si attiva <b>automaticamente</b> e ti inviamo
                                                un'<b>email di conferma</b>.
                                            </li>
                                            <li>
                                                Solo a quel punto ti chiederemo i <b>dati di fatturazione</b> per
                                                emettere la fattura: <b>non devi inserirli adesso</b>.
                                            </li>
                                        </ol>
                                        <p className="mb-0 text-muted">
                                            Puoi chiudere questa pagina: l'attivazione avviene da sola a pagamento
                                            ricevuto, non serve restare in attesa qui.
                                        </p>
                                    </Alert>
                                    <Button
                                        variant="outline-secondary"
                                        className="w-100 mt-3"
                                        onClick={handleLogout}
                                    >
                                        Esci (Logout)
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <div className="mb-4 text-center">
                                        <h4 className="mb-3">Benvenuto nel futuro dello sport!</h4>
                                        <p className="text-muted mb-2">Grazie per aver scelto SportId Manager®.</p>
                                        <p className="text-muted mb-2">Il tuo acquisto della promo <b>STARTUP ALL INCLUSIVE</b> è andato a buon fine. Sei ufficialmente a bordo!</p>
                                        <p className="text-muted mb-0">Abbiamo una splendida notizia per te: grazie a questa offerta limitata, ti sei assicurato il <b>prezzo bloccato di € 149/anno per sempre</b>. Questo significa che, finché deciderai di restare con noi, non pagherai mai la tariffa standard di € 499/anno. Un risparmio netto di ben <b>€ 350 ogni singolo anno!</b></p>
                                    </div>

                                    <p className="text-center text-muted mb-4">
                                        Per completare l'attivazione, inserisci i dati di fatturazione.
                                    </p>

                                    {error && <Alert variant="danger">{error}</Alert>}

                                    <Form onSubmit={handleSubmit}>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Denominazione *</Form.Label>
                                                    <Form.Control required type="text" name="denomination" value={formData.denomination} onChange={handleChange} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Partita IVA</Form.Label>
                                                    <Form.Control type="text" name="vatNumber" value={formData.vatNumber} onChange={handleChange} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Codice Fiscale *</Form.Label>
                                                    <Form.Control required type="text" name="taxCode" value={formData.taxCode} onChange={handleChange} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Codice SDI{' '}
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Inserisci 7 volte 0 se non sei in possesso di codice SDI</Tooltip>}>
                                                            <i className="fa fa-info-circle text-muted" style={{ cursor: 'pointer' }} />
                                                        </OverlayTrigger>
                                                    </Form.Label>
                                                    <Form.Control type="text" name="sdiCode" value={formData.sdiCode} onChange={handleChange} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>PEC {type === 'business' ? '*' : ''}</Form.Label>
                                                    <Form.Control required={type === 'business'} type="email" name="pec" value={formData.pec} onChange={handleChange} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Indirizzo *</Form.Label>
                                                    <Form.Control required type="text" name="address" value={formData.address} onChange={handleChange} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>CAP *</Form.Label>
                                                    <Form.Control required type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Città *</Form.Label>
                                                    <Form.Control required type="text" name="city" value={formData.city} onChange={handleChange} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Provincia (Sigla) *</Form.Label>
                                                    <Form.Control required type="text" maxLength={2} name="province" value={formData.province} onChange={handleChange} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label>Nazione</Form.Label>
                                                    <Form.Control type="text" value="Italia" readOnly />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Button variant="primary" type="submit" className="w-100" disabled={loading}>
                                            {loading ? 'Salvataggio...' : 'Salva e Continua'}
                                        </Button>
                                    </Form>

                                    <Button 
                                        variant="outline-secondary" 
                                        className="w-100 mt-3"
                                        onClick={handleLogout}
                                        disabled={loading}
                                    >
                                        Esci (Logout)
                                    </Button>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default SuccessPage;